import { Box, Grid, Tab } from "@mzc-pdc/ui";
import { StyledTabs } from "./tabs.styled";
import Typography from "@components_v2/typography/typography";
import theme from "@styles/theme";
import { useTranslation } from "react-i18next";
import { TabScrollButton } from "@mui/material";
import { forwardRef, memo, useCallback, useMemo, useRef } from "react";

export const TabPanel = memo((props) => {
    const { children, value, index, sx, ...other } = props;
    return (
        <Box role="tabpanel" key={`tab-panel-${index}`} hidden={value !== index} sx={{ ...sx }} {...other}>
            {children}
        </Box>
    );
});

// justifyContent 가 없으면 flex-start 라, MUI Tab 의 min-width(90px)보다 짧은 라벨이
// 안쪽 상자(48px) 왼쪽에 붙는다. 실측 — `개요`(26px)는 11px 왼쪽으로 쏠렸고 `AI 분석`(42px)은 3px,
// 상자보다 넓은 `메타데이터`(64px)·`활동 내역`(55px)은 상자가 글자에 맞게 늘어나 원래 가운데였다.
// 즉 짧은 라벨만 쏠려서 탭바가 들쭉날쭉해 보인다.
const TabLabel = memo(({ label, count, icon, iconPosition }) => (
    <Grid container flexDirection={"row"} alignItems={"center"} justifyContent={"center"}>
        {label}
        {count !== undefined && (
            <Grid ml={1}>
                <Typography variant={"body1_700"} color={theme.palette.primary[800]} children={count} />
            </Grid>
        )}
        {icon && <Grid ml={1}>{icon}</Grid>}
    </Grid>
));

export const Tabs = memo(
    forwardRef(
        (
            {
                items,
                value,
                type,
                orientation,
                textColor = "secondary",
                indicatorColor = "secondary",
                indicatorPosition = "bottom",
                TabIndicatorProps,
                isBordered = true,
                variant,
                onChangeTab,
                scrollButtons,
                scrollButtonPosition,
                /*
                 * 화살표를 누를 때 무엇을 하나.
                 *   `"scroll"`(기본) 탭 줄을 민다 — MUI 기본 동작.
                 *   `"changeTab"`      선택된 탭을 이전/다음으로 옮긴다.
                 * 기본값을 `"scroll"` 로 둔 것은 **기존 사용처의 동작을 바꾸지 않기 위해서**다.
                 */
                scrollButtonAction = "scroll",
                setRef,
                actionButton,
                divider,
                sx,
            },
            ref,
        ) => {
            const { t } = useTranslation();

            /*
             * 🔴 **화살표를 「이전 탭 / 다음 탭」으로 쓰는 경우의 구현.**
             *
             * MUI 의 기본 스크롤 버튼은 선택을 그대로 둔 채 탭 줄만 민다. 「좌우 탭 탐색」이 의도인 화면에서는
             * 그게 아니라 **선택된 탭이 옆으로 옮겨가야** 하고, 비활성 조건도 스크롤 끝이 아니라
             * **선택된 탭이 처음/마지막일 때**다(기본 동작은 첫 탭이 다 «보이기만» 해도 `‹` 를 꺼서,
             * 아직 첫 탭으로 가지 않았는데 되돌아갈 수단이 사라진 것처럼 보인다).
             *
             * 이 컴포넌트는 `items`·`value`·`onChangeTab` 을 이미 갖고 있으므로 **호출부가 목록을 다시 넘길 필요가 없다.**
             *
             * ⚠️ 화살표가 «나타나는» 조건은 그대로 `scrollButtons` 가 정한다(`"auto"` 면 넘칠 때만 생긴다).
             *    「탭 이동」과 「스크롤이 필요한가」는 서로 다른 신호인데 지금은 후자가 전자의 노출을 정한다.
             *    넘침이 있는 한 성립하는 관계일 뿐이므로, 탭 구성이 바뀌면 이 전제부터 다시 본다.
             *
             * ⚠️ 참조를 `useMemo(…, [])` 로 «고정»한다. 렌더마다 새로 만들면 탭을 누를 때마다 화살표가 다시 마운트된다.
             *    그래서 바뀌는 값은 ref 로 읽는다. 렌더 «중»에 ref 를 변형하는데, 이 저장소는 React 17
             *    (비-Concurrent)이라 렌더가 중단·재시작되지 않아 최신값으로 덮이는 것이 보장된다.
             *    **React 18 Concurrent 로 올라가면 이 전제가 깨진다.**
             */
            const tabNavRef = useRef({ items: [], value: null, onChangeTab: null, t: null });
            tabNavRef.current = { items, value, onChangeTab, t };

            const TabNavButton = useMemo(
                () =>
                    function TabNavButton(props) {
                        const { items: list, value: active, onChangeTab: change, t: tr } = tabNavRef.current;
                        const values = (list ?? []).map((item) => item.value);
                        const index = values.indexOf(active);
                        const isPrev = props.direction === "left";
                        const target = isPrev ? index - 1 : index + 1;
                        const atEdge = index < 0 || target < 0 || target >= values.length;
                        /*
                         * 🔴 **스프레드 «뒤»에 두어 MUI 가 박아 넣은 값을 덮는다.**
                         *
                         * `TabScrollButton` 은 `role: null`·`tabIndex: null` 을 하드코딩하는데
                         * (`TabScrollButton.js:96-98`), 그 뒤에 `other` 를 스프레드하므로(같은 파일 100 행)
                         * **여기서 주는 값이 항상 이긴다.** `disabled`·`onClick` 도 같은 방식이다.
                         *
                         * **왜 이름을 붙이나** — 화살표가 「스크롤 편의」가 아니라 **넘친 탭에 마우스로 닿는 유일한 경로**가 된다.
                         * `common::label::Previous`·`Next` 는 5 개 언어에 이미 있어 새 키가 필요 없다.
                         * 키보드 활성화는 `ButtonBase` 가 비-button 요소에 Enter·Space 를 이어 준다(`ButtonBase.js:227·248`).
                         */
                        return (
                            <TabScrollButton
                                {...props}
                                role={"button"}
                                tabIndex={atEdge ? -1 : 0}
                                aria-label={
                                    isPrev ? tr(`common::label::Previous`, `Previous`) : tr(`common::label::Next`, `Next`)
                                }
                                aria-disabled={atEdge}
                                disabled={atEdge}
                                onClick={() => {
                                    if (!atEdge) change(values[target]);
                                }}
                            />
                        );
                    },
                [],
            );

            const handleChange = useCallback(
                (e, v) => {
                    onChangeTab(v);
                },
                [onChangeTab],
            );

            const tabItems = useMemo(
                () =>
                    items?.map((item, index) => (
                        <Tab
                            key={item.value || index}
                            iconPosition={item.iconPosition}
                            label={
                                <TabLabel
                                    label={item.label}
                                    count={item.count}
                                    icon={item.icon}
                                    iconPosition={item.iconPosition}
                                />
                            }
                            value={item.value}
                            disabled={item.disabled}
                            ref={(ref) => setRef && setRef(ref, item)}
                        />
                    )),
                [items],
            );

            return (
                <StyledTabs
                    ref={ref}
                    value={value}
                    onChange={handleChange}
                    orientation={orientation}
                    type={type}
                    divider={divider}
                    textColor={textColor}
                    indicatorColor={indicatorColor}
                    indicatorPosition={indicatorPosition}
                    TabIndicatorProps={TabIndicatorProps}
                    isBordered={isBordered}
                    variant={variant}
                    scrollButtons={scrollButtons}
                    scrollButtonPosition={scrollButtonPosition}
                    ScrollButtonComponent={scrollButtonAction === "changeTab" ? TabNavButton : undefined}
                    sx={sx}
                >
                    {tabItems}
                    {actionButton}
                </StyledTabs>
            );
        },
    ),
);
